/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import { Text, TouchableOpacity, View, type ViewStyle } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { OperationalFilterSectionDivider } from './OperationalFilterPrimitives';
import { operationalFilterStyles as styles } from './operationalFilters.styles';
import { colors } from '../theme/public-api';

type Props = {
  title: string;
  hint?: string;
  hintCompact?: string;
  isMobile?: boolean;
  headerAction?: React.ReactNode;
  banner?: React.ReactNode;
  showDividerBeforeBody?: boolean;
  children: React.ReactNode;
  bodyStyle?: ViewStyle;
  /** Panel colapsable: muestra el título y un toggle; oculta el body. */
  collapsible?: boolean;
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  collapsedActiveFilterCount?: number;
};

/** Cabecera de panel operativo (título + hint + acción) dentro de OperationalFiltersCard. */
export function OperationalFiltersPanelShell({
  title,
  hint,
  hintCompact,
  isMobile = false,
  headerAction,
  banner,
  showDividerBeforeBody = true,
  children,
  bodyStyle,
  collapsible = false,
  collapsed = false,
  onToggleCollapsed,
  collapsedActiveFilterCount = 0,
}: Props) {
  const titleRow = collapsible ? (
    <TouchableOpacity
      style={styles.titleRow}
      onPress={onToggleCollapsed}
      accessibilityRole="button"
      accessibilityLabel={collapsed ? 'Expandir filtros' : 'Colapsar filtros'}
      accessibilityState={{ expanded: !collapsed }}
    >
      <View style={styles.titleCol}>
        <View style={styles.collapseHeaderMain}>
          <Text style={styles.title}>{title}</Text>
          {collapsed && collapsedActiveFilterCount > 0 ? (
            <View style={styles.collapsedActiveFiltersChip}>
              <Text style={styles.collapsedActiveFiltersChipText}>
                {collapsedActiveFilterCount} activo{collapsedActiveFilterCount === 1 ? '' : 's'}
              </Text>
            </View>
          ) : null}
        </View>
        {isMobile && hintCompact ? (
          <Text style={styles.hintCompact} numberOfLines={1}>
            {hintCompact}
          </Text>
        ) : hint ? (
          <Text style={styles.hint}>{hint}</Text>
        ) : null}
      </View>
      <Ionicons name={collapsed ? 'chevron-down' : 'chevron-up'} size={18} color={colors.textMuted} />
      {headerAction}
    </TouchableOpacity>
  ) : (
    <View style={styles.titleRow}>
      <View style={styles.titleCol}>
        <Text style={styles.title}>{title}</Text>
        {isMobile && hintCompact ? (
          <Text style={styles.hintCompact} numberOfLines={1}>
            {hintCompact}
          </Text>
        ) : hint ? (
          <Text style={styles.hint}>{hint}</Text>
        ) : null}
      </View>
      {headerAction}
    </View>
  );

  const bodyVisible = !collapsible || !collapsed;

  return (
    <>
      {titleRow}
      {banner}
      {bodyVisible && showDividerBeforeBody ? <OperationalFilterSectionDivider /> : null}
      {bodyVisible ? <View style={bodyStyle}>{children}</View> : null}
    </>
  );
}
